import { useCallback } from 'react';

import { Currency, MonetaryAmount } from '__generated__/globalTypes';
import { useConfigContext } from 'contexts/config';
import { useCurrentUserContext } from 'contexts/currentUser';
import {
  MonetaryAmount as MonetaryAmountClass,
  MonetaryAmountParams,
  getFiatMonetaryAmountIndex,
} from 'lib/monetaryAmount';
import { fromWei } from 'lib/wei';

type NonNullableObj<T> = { [K in keyof T]: NonNullable<T[K]> };

export type MonetaryAmountOutput = Omit<
  NonNullableObj<Required<MonetaryAmount>>,
  '__typename' | 'referenceCurrency'
>;

export const zeroMonetaryAmount: MonetaryAmountOutput = {
  eur: 0,
  usd: 0,
  gbp: 0,
  wei: 0n,
};

export const isEqual = (a: MonetaryAmount, b: MonetaryAmount) => {
  return a.eur === b.eur && a.usd === b.usd && a.gbp === b.gbp;
};

export const Sum = (amounts: MonetaryAmountOutput[]) => {
  return amounts.reduce((acc, amount) => {
    return {
      eur: acc.eur + amount.eur,
      usd: acc.usd + amount.usd,
      gbp: acc.gbp + amount.gbp,
      wei: acc.wei + amount.wei,
    };
  }, zeroMonetaryAmount);
};

export const calculateFees = ({
  monetaryAmount,
  feesPercentagePoints,
}: {
  monetaryAmount: MonetaryAmountOutput;
  feesPercentagePoints: bigint;
}) => {
  const feesMonetaryAmount =
    feesPercentagePoints === 0n
      ? zeroMonetaryAmount
      : {
          eur: (BigInt(monetaryAmount.eur) * feesPercentagePoints) / 100n,
          usd: (BigInt(monetaryAmount.usd) * feesPercentagePoints) / 100n,
          gbp: (BigInt(monetaryAmount.gbp) * feesPercentagePoints) / 100n,
          wei: (monetaryAmount.wei * feesPercentagePoints) / 100n,
        };
  const youReceiveMonetaryAmount = {
    eur: BigInt(monetaryAmount.eur) - BigInt(feesMonetaryAmount.eur),
    usd: BigInt(monetaryAmount.usd) - BigInt(feesMonetaryAmount.usd),
    gbp: BigInt(monetaryAmount.gbp) - BigInt(feesMonetaryAmount.gbp),
    wei: monetaryAmount.wei - feesMonetaryAmount.wei,
  };

  return {
    feesMonetaryAmount,
    youReceiveMonetaryAmount,
  };
};

export const useMonetaryAmount = () => {
  const { exchangeRate } = useConfigContext();
  const {
    fiatCurrency: { code },
    currency,
  } = useCurrentUserContext();

  const toMonetaryAmount = useCallback(
    (params: MonetaryAmountParams): MonetaryAmountOutput => {
      const monetaryAmount = new MonetaryAmountClass(params);

      return monetaryAmount.inCurrencies(exchangeRate.ethRates);
    },
    [exchangeRate.ethRates]
  );

  const getUserMonetaryAmount = useCallback(
    (amount: number | bigint) =>
      toMonetaryAmount({
        [code.toLowerCase()]: amount,
        referenceCurrency: code,
      }),
    [toMonetaryAmount, code]
  );

  const getUserFiatAmount = useCallback(
    (monetaryAmount: MonetaryAmountOutput) =>
      monetaryAmount[getFiatMonetaryAmountIndex(code)],
    [code]
  );

  const getUserAmount = useCallback(
    (monetaryAmount: MonetaryAmountOutput) =>
      currency === Currency.FIAT
        ? getUserFiatAmount(monetaryAmount) / 100
        : fromWei(monetaryAmount.wei),
    [currency, getUserFiatAmount]
  );

  return {
    toMonetaryAmount,
    getUserFiatAmount,
    getUserAmount,
    getUserMonetaryAmount,
  };
};
